import { action, computed, makeAutoObservable } from 'mobx';
import { cloneDeep } from 'lodash';
import {
    PromotedEventsState,
    CompetitionCombinedType,
    EventCombinedType,
} from 'src/AppState/CmsState/PromotedEventsState/PromotedEventsState';
import { arrayMove } from 'react-movable';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { TrpcClient } from 'src/AppState/TrpcClient';

export class PromotedEventsListState {
    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly trpcClient: TrpcClient,
        private readonly promotedEventsSectionState: PromotedEventsState,
        private readonly sportId: string,
        private readonly competitionData: CompetitionCombinedType
    ) {
        makeAutoObservable(this);
    }

    @computed public get isAnyEvents(): boolean {
        return this.competitionData.events_list.some((event) => {
            const timeMatchStartedAndFinished = this.sdkStaff.models.getEvent(
                event.event_id
            )?.timeMatchStartedAndFinished;

            if (timeMatchStartedAndFinished !== undefined) {
                return timeMatchStartedAndFinished === false;
            }

            return false;
        });
    }

    @computed public get competitionName(): string {
        return this.sdkStaff.models.getCompetitionModel(this.competitionData.competition_id)?.name ?? '';
    }

    filterElements = (event: EventCombinedType, oldIndex: number, newIndex: number): boolean => {
        if (oldIndex > newIndex) {
            return event.display_order >= newIndex && event.display_order < oldIndex;
        } else {
            return event.display_order <= newIndex && event.display_order > oldIndex;
        }
    };

    updateDisplayOrder = (
        initialArray: Array<EventCombinedType>,
        oldIndex: number,
        newIndex: number
    ): Array<EventCombinedType> => {
        const tempClonedArray = cloneDeep(initialArray);
        const promotedEventsToMove = initialArray
            .filter((comp) => this.filterElements(comp, oldIndex, newIndex))
            .map((c) => c.id);
        const shouldIncrementIndexes = oldIndex > newIndex;
        const draggedEvent = initialArray.find((comp) => comp.display_order === oldIndex);

        for (let i = 0; i < tempClonedArray.length; i++) {
            const event = tempClonedArray[i];
            if (event === undefined) {
                return tempClonedArray;
            }
            if (promotedEventsToMove.includes(event.id)) {
                if (shouldIncrementIndexes) {
                    event.display_order = event.display_order + 1;
                } else {
                    event.display_order = event.display_order - 1;
                }
            }

            if (event.id === draggedEvent?.id) {
                event.display_order = newIndex;
            }
        }
        return tempClonedArray;
    };

    @action handleEventsOrder = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
    }): Promise<void> => {
        const tempEventsData =
            this.promotedEventsSectionState.getEventsArrayForDraggableList(
                this.sportId,
                this.competitionData.competition_id
            ) ?? [];
        const displayOrderOfOldIndexElement = tempEventsData[oldIndex]?.display_order;
        const displayOrderOfNewIndexElement = tempEventsData[newIndex]?.display_order;
        const draggedEvent = tempEventsData[oldIndex];

        if (displayOrderOfNewIndexElement === undefined || displayOrderOfOldIndexElement === undefined) {
            return;
        }
        if (draggedEvent === undefined) {
            return;
        }

        const arrayMoved = arrayMove(tempEventsData, oldIndex, newIndex);

        const arrayWithUpdatedDisplayOrder = this.updateDisplayOrder(
            arrayMoved,
            displayOrderOfOldIndexElement,
            displayOrderOfNewIndexElement
        );

        const newOrder = this.promotedEventsSectionState.setEventsArrayForDraggableList(
            this.sportId,
            this.competitionData.competition_id,
            arrayWithUpdatedDisplayOrder
        );
        this.promotedEventsSectionState.setSportsData(newOrder);

        await this.trpcClient.client.promotedEvents.changePromotedEventsOrder.mutate({
            data: {
                id: draggedEvent.id,
                new_order: displayOrderOfNewIndexElement,
                old_order: displayOrderOfOldIndexElement,
                sport_id: this.sportId,
                competition_id: this.competitionData.competition_id,
            },
        });

        this.promotedEventsSectionState.promotedEventsResource.refresh();
    };
}
